// Live voice with VERA.
// VERA Studio sends its WebRTC offer to the control plane (POST /v1/realtime/calls) with the person's Microsoft sign-in.
// The control plane sets VERA's identity, honesty rules and crisis protocol on the server, opens a short-lived voice
// session on VERANeural's Azure AI Foundry realtime deployment and returns the answer. Audio then flows directly.
// No Azure key is ever in the app.

import { config } from "../config/env";
import type { VoiceTools } from "./voiceTools";

export type VoiceEvent =
  | { type: "you"; text: string; final: boolean }
  | { type: "vera"; text: string; final: boolean }
  | { type: "state"; state: "connecting" | "listening" | "speaking" | "ended" }
  | { type: "error"; message: string };

// One live voice at a time, in the whole app: starting one hangs up any other (the Talk button in the field bar and the
// composer's microphone are separate, and two at once meant hearing two VERAs).
let current: { stop: () => void } | null = null;

/** Hangs up the live voice that's running, if any. */
export function stopLiveVoice() { current?.stop(); }

export async function startLiveVoice(opts: { mic: MediaStream; workspaceId: string; getToken: () => Promise<string | null>; onEvent: (e: VoiceEvent) => void; audio: HTMLAudioElement; lang?: string; tools?: VoiceTools | null }) {
  const { mic, workspaceId, getToken, onEvent, audio, tools } = opts;
  stopLiveVoice();
  onEvent({ type: "state", state: "connecting" });
  const token = await getToken();
  if (!token) throw new Error("Please sign in again so VERA can verify who you are.");

  const pc = new RTCPeerConnection();
  // Claimed at once, so a second start while this one is still connecting hangs this one up.
  let stopped = false;
  const session = { stop: () => { if (stopped) return; stopped = true; if (current === session) current = null; try { dc.close(); } catch { /* not open */ } pc.close(); onEvent({ type: "state", state: "ended" }); } };
  current = session;
  pc.ontrack = (e) => { audio.srcObject = e.streams[0]; void audio.play(); };
  mic.getAudioTracks().forEach((t) => pc.addTrack(t, mic));
  const dc = pc.createDataChannel("oai-events");
  const send = (event: object) => { if (dc.readyState === "open") dc.send(JSON.stringify(event)); };
  // What she can look up (the person's connected Microsoft 365), run here with their own sign-in. See voiceTools.ts.
  dc.onopen = () => {
    if (!tools) return;
    send({ type: "session.update", session: { type: "realtime", tools: tools.defs, tool_choice: "auto" } });
    send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{ type: "input_text", text: tools.guidance }] } });
  };
  const running = new Set<string>();
  let veraText = "";
  dc.onmessage = (m) => {
    try {
      const ev = JSON.parse(String(m.data)) as { type: string; delta?: string; transcript?: string; name?: string; call_id?: string; arguments?: string };
      if (ev.type === "response.function_call_arguments.done" && tools && ev.call_id && ev.name && !running.has(ev.call_id)) {
        const callId = ev.call_id;
        running.add(callId);
        void tools.run(ev.name, ev.arguments ?? "{}", callId)
          .catch((e: Error) => JSON.stringify({ error: `Couldn't look that up: ${e.message}` }))
          .then((output) => {
            send({ type: "conversation.item.create", item: { type: "function_call_output", call_id: callId, output } });
            send({ type: "response.create" });
          });
        return;
      }
      if (ev.type === "input_audio_buffer.speech_started") onEvent({ type: "state", state: "listening" });
      if (ev.type === "conversation.item.input_audio_transcription.completed" && ev.transcript) onEvent({ type: "you", text: ev.transcript, final: true });
      if ((ev.type === "response.audio_transcript.delta" || ev.type === "response.output_audio_transcript.delta") && ev.delta) {
        veraText += ev.delta; onEvent({ type: "state", state: "speaking" }); onEvent({ type: "vera", text: veraText, final: false });
      }
      if (ev.type === "response.audio_transcript.done" || ev.type === "response.output_audio_transcript.done" || ev.type === "response.done") {
        if (veraText) onEvent({ type: "vera", text: veraText, final: true });
        veraText = ""; onEvent({ type: "state", state: "listening" });
      }
    } catch { /* ignore malformed events */ }
  };
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  const lang = opts.lang || (navigator.language || "en").slice(0, 2);
  // `tools` tells VERA's server what she can look up, so her standing instructions match.
  const lookups = tools ? `&tools=${encodeURIComponent(tools.groups.join(","))}` : "";
  const answer = await fetch(`${config.controlPlaneUrl}/v1/realtime/calls?workspaceId=${encodeURIComponent(workspaceId)}&lang=${encodeURIComponent(lang)}${lookups}`, {
    method: "POST", body: offer.sdp, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/sdp" },
  });
  // Hung up while connecting (another voice started, or the window closed): nothing more to do.
  if (stopped) return session;
  if (!answer.ok) {
    stopped = true;
    if (current === session) current = null;
    pc.close();
    const detail = await answer.json().catch(() => ({} as { error?: string }));
    throw new Error(answer.status === 401 ? "Please sign in again so VERA can verify who you are."
      : answer.status === 403 ? "Live voice isn't enabled for your role yet."
      : detail.error || "Live voice is unavailable right now.");
  }
  const sdp = await answer.text();
  if (stopped) return session;
  await pc.setRemoteDescription({ type: "answer", sdp });
  onEvent({ type: "state", state: "listening" });
  return session;
}
